/**
 * FleetAppsDialog.tsx — v2.4.0
 *
 * The system-wide roll-up of the app inventory: which apps exist across the
 * fleet, and — the reason this exists — WHICH DEVICES CARRY A KNOWN PROBLEM APP.
 * If a tech keeps going dark, this is the first place to look.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { devicesApi } from '../../services/api';

const RISK_COLOR: Record<string, 'error' | 'warning' | 'info'> = {
  mock_location: 'error', battery_killer: 'warning', task_killer: 'warning', vpn: 'info',
};
const fmt = (s?: string | null) => {
  if (!s) return '—';
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

export default function FleetAppsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await devicesApi.appsRollup();
      setData(r.data);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not load the fleet app roll-up');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { if (open) void load(); }, [open, load]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={phone}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          Apps across the fleet
          <Typography variant="caption" display="block" color="text.secondary">
            Which apps are on the phones — and which of them are strangling GPS.
          </Typography>
        </Box>
        <IconButton onClick={load}>{busy ? <CircularProgress size={20} /> : <RefreshIcon />}</IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {!data ? <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box> : (
          <>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {data.by_risk?.length ? data.by_risk.map((r: any) => (
                <Grid item xs={6} sm={3} key={r.risk}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Chip size="small" color={RISK_COLOR[r.risk] || 'default'} label={r.label} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>{r.count}</Typography>
                    <Typography variant="caption" color="text.secondary">installs across the fleet</Typography>
                  </Paper>
                </Grid>
              )) : (
                <Grid item xs={12}>
                  <Alert severity="success">No known problem apps detected on any reporting device.</Alert>
                </Grid>
              )}
            </Grid>

            {data.never_reported?.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <b>{data.never_reported.length} device(s) have never posted an app inventory</b> —{' '}
                {data.never_reported.map((d: any) => d.employee_name || d.device_id).join(', ')}. They are
                either on an older tracker build or have not been granted package visibility. Their phones
                could be running a battery optimizer and we would not know.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Problem apps by device ({data.problem_count})
            </Typography>
            {data.problem_apps?.length ? (
              <Table size="small" sx={{ mb: 3 }} data-testid="fleet-problem-apps">
                <TableHead><TableRow>
                  <TableCell>Employee / device</TableCell><TableCell>App</TableCell>
                  <TableCell>Risk</TableCell><TableCell>Why it matters</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.problem_apps.map((a: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {a.employee_name || 'Unpaired'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {a.device_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {a.app_label || a.package_name}
                        <Typography variant="caption" display="block" color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}>{a.package_name}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" color={RISK_COLOR[a.risk] || 'default'} label={a.risk_label || a.risk} /></TableCell>
                      <TableCell><Typography variant="caption">{a.risk_reason}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>None found.</Typography>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Per-device permission state</Typography>
            {data.device_states?.length ? (
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead><TableRow>
                  <TableCell>Device</TableCell><TableCell>Apps seen</TableCell>
                  <TableCell>Package visibility</TableCell><TableCell>Usage stats</TableCell>
                  <TableCell>Battery-opt exempt</TableCell><TableCell>Mock location</TableCell>
                  <TableCell>Reported</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.device_states.map((s: any) => (
                    <TableRow key={s.device_id} hover>
                      <TableCell>{s.employee_name || s.device_id}</TableCell>
                      <TableCell>{s.reported_count ?? 0}{s.total_packages ? ` / ${s.total_packages}` : ''}</TableCell>
                      <TableCell>{s.query_all_packages === null ? '—'
                        : <Chip size="small" color={s.query_all_packages ? 'success' : 'warning'}
                            label={s.query_all_packages ? 'granted' : 'denied'} />}</TableCell>
                      <TableCell>{s.usage_stats_granted === null ? '—'
                        : <Chip size="small" color={s.usage_stats_granted ? 'success' : 'warning'}
                            label={s.usage_stats_granted ? 'granted' : 'denied'} />}</TableCell>
                      <TableCell>{s.battery_optimization_exempt === null ? '—'
                        : <Chip size="small" color={s.battery_optimization_exempt ? 'success' : 'error'}
                            label={s.battery_optimization_exempt ? 'exempt' : 'THROTTLED'} />}</TableCell>
                      <TableCell>{s.mock_location_app
                        ? <Chip size="small" color="error" label={s.mock_location_app} /> : '—'}</TableCell>
                      <TableCell>{fmt(s.collected_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>No device has reported yet.</Typography>}

            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Most common apps ({data.common_apps?.length || 0})
            </Typography>
            {data.common_apps?.length ? (
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    <TableCell>App</TableCell><TableCell>Devices</TableCell><TableCell>Flag</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {data.common_apps.map((a: any) => (
                      <TableRow key={a.package_name} hover>
                        <TableCell>
                          {a.app_label || a.package_name}
                          <Typography variant="caption" display="block" color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}>
                            {a.package_name}{a.is_system ? ' • system' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>{a.devices}</TableCell>
                        <TableCell>{a.risk
                          ? <Chip size="small" color={RISK_COLOR[a.risk] || 'default'} label={a.risk_label || a.risk} />
                          : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : <Typography variant="body2" color="text.secondary">Nothing reported yet.</Typography>}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
